/**
 * NOTE（2026-07-03）：最终实现放弃了下方“Option A：复用 base 类 + 挂载弹窗”的方案（会导致费用页签
 * 常驻 billDialog/overseaBillingDialog 重组件，叠加 keep-alive 引发 OOM）。现状是：
 *   - 费用页签（tabChargesHP.createBillingFromCharges）自包含建账，自己组装 billing 数据并直接调
 *     saveBilling / saveOverseaBilling，非关键字段用默认值；不再实例化 pm/pmOversea，不再挂载弹窗。
 *   - internalBillInfoBase / overseaBillingBase 已滚回到本功能之前的状态。
 * 本文件保留下方的纯函数（groupChargesForBilling / buildDirectInternalBillingPayload /
 * buildDirectOverseaBillingPayload / resolveOverseaSettlementAgentId / isChargeBillable）供自包含流程复用。
 * 下面的 P2-T1 审计文档仅作历史记录，描述的是已被废弃的耦合方案，请勿据此实现。
 *
 * === Coupling audit (P2-T1) ===
 * Contract tabCharges must satisfy to drive the create-bill base classes
 * (Option A: tabCharges owns its own pm / pmOversea instance with view = tabCharges,
 *  plus its own bill/settlement/oversea dialog state + dialog components mounted in template).
 *
 * All file:line references are relative to the worktree root.
 * Base-class instances store a reference to the Vue component as `this.view`.
 * Therefore EVERY `this.view.<prop>` below is a property the VIEW (here: tabCharges) MUST expose,
 * and EVERY `this.<prop>` is internal base-class state (tabCharges does NOT need to provide it —
 * the base class sets it on itself; but the caller must seed a few of them before kicking off).
 *
 * Legend for the "tabCharges?" column:
 *   HAS                = tabCharges already provides this on `this`
 *   MUST ADD (data)    = tabCharges must add a reactive `data()` field with this name/shape
 *   MUST ADD (computed)= tabCharges must add a computed/prop with this name
 *   UNCLEAR            = bill-tab-specific; tabCharges cannot trivially provide it — see notes
 *
 * ----------------------------------------------------------------------------
 * DOMESTIC path (InternalBillInfoBase):
 *   entry: caller sets this._isAr, then addRoleCharge(charges)
 *          -> getChargeAccounts / getChargeSettlements
 *          -> loadAccountCompany(acountId, createBillWay)
 *          -> openBillingWindow(company)
 *
 * this.<prop> (base-class instance state; NOT required on the view, but caller may seed):
 *   - _isAr              (write, by caller)  internalBillInfoBase.js:628 / ctor :58
 *       boolean; receivable flag. tabCharges MUST set pm._isAr before calling addRoleCharge. (HAS via pm)
 *   - _selectedAcountId  (write)             internalBillInfoBase.js:869  — number; chosen settlement object cmpIdx
 *   - _createBillWay     (write)             internalBillInfoBase.js:870,882 — 0|1; 0=by account, 1=by settlement agent
 *   - billCharges        (r/w)               internalBillInfoBase.js:821,823,825,829 / getChargeAccounts :1000 / getChargeSettlements :1021 / openBillingWindow :884
 *       charge[]; set inside addRoleCharge from the input `charges` (filtered). NOT seeded by caller — addRoleCharge builds it.
 *   - enableCommission   (read)              internalBillInfoBase.js:976  — boolean (ctor :81). tabCharges path only reads it; safe if left false.
 *   - currentBillingBranchId (read)          internalBillInfoBase.js:976  — number (ctor :89)
 *   - departmentTeamUser (read)              internalBillInfoBase.js:977  — userId[]|null (ctor :65)
 *   - $checkPermission   (read)              internalBillInfoBase.js:803,805 — fn; bound from view in ctor :56
 *   - $message           (read)              internalBillInfoBase.js:826,831,859 — bound from view in ctor :49
 *   - $confirm           (read)              internalBillInfoBase.js:950 — bound from view in ctor :50
 *
 * this.view.<prop> (VIEW surface — tabCharges MUST provide):
 *   - view.$t            (read)              internalBillInfoBase.js:826,831,854,859,902,950,951,952,953,973, etc.
 *       i18n fn.                                                tabCharges? HAS (Vue instance has $t)
 *   - view.$message      (read)              internalBillInfoBase.js:826,831,859 (also via this.$message),902,973
 *       ElementUI message.                                      tabCharges? HAS (Vue prototype)
 *   - view.settlementSelectDialog (write .accounts/.settlements/.title/.visible)
 *                                                   internalBillInfoBase.js:852-855
 *       object { visible, title, accounts, settlements }.     tabCharges? MUST ADD (data) — see shape below
 *   - view.billDialog    (write .createBillWay/.accounts/.billingUuid/.isView/.newBilling/.newCharges/.currentBillingBranchId/.departmentTeamUser/.slaveCharges/.instructionBtns/.visible, read .newBilling.currency)
 *                                                   internalBillInfoBase.js:916-990
 *       object.                                                 tabCharges? MUST ADD (data) — see shape below
 *   - view.newBillDialog (write .customerDetail/.visible)
 *                                                   internalBillInfoBase.js:987-988
 *       object { visible, customerDetail }.                    tabCharges? MUST ADD (data) — see shape below
 *
 *   NOTE: The domestic create path does NOT read view.isEdit / view.job / view.up.gridApi /
 *   view.selectedBilling — those are only on the edit/view/delete branches and on addBillingHandler
 *   (the caller). The create-from-charges flow (addRoleCharge down) is self-contained given the
 *   four dialog-state objects above plus the standard Vue instance ($t/$message/$confirm/$store).
 *   This.job is read by getInternalCharges()/addBillingHandler, but Option A will feed charges
 *   directly into addRoleCharge(charges), so view.job is NOT strictly required for the create path —
 *   UNLESS tabCharges also wants the loadAccountCompany/openBillingWindow side-effects, which only
 *   touch dialog state. Keep view.job available anyway (pm.job getter = view.job, ctor family).
 *
 * ----------------------------------------------------------------------------
 * OVERSEAS path (OverseaBillingBase):
 *   entry: caller sets this._billingCharges = charges, then checkRoleBillingCharge()
 *          -> openBillingWindow(accountId, createBillWay)
 *          -> getBilling(accountId, createBillWay)
 *
 * this.<prop> (base-class instance state):
 *   - _billingCharges    (write, by caller)  overseaBillingBase.js:730 (onAddClickEvent), then read :818,829,866,869,881,903,942,959,963
 *       charge[]; caller (tabCharges) MUST set pm._billingCharges before checkRoleBillingCharge(). (HAS via pm)
 *   - $message           (read)              overseaBillingBase.js:819,855,933 — bound from view ctor :63
 *   - $confirm           (read)              overseaBillingBase.js:915 — bound from view ctor :65
 *   - _billingChargeAccountcodes (write)     overseaBillingBase.js:940,949,953,1006,1007 — {label,data}[] built in openBillingWindow
 *   - isAddBillTaxAmount (read)              overseaBillingBase.js:979 — boolean (loadConfigValuesHandler :267)
 *   - debitChargetaxRate (read)              overseaBillingBase.js:980 — number (loadConfigValuesHandler :268)
 *   - enableCommission   (read)              overseaBillingBase.js:977 — boolean (ctor :95)
 *   - currentBillingBranchId (read)          overseaBillingBase.js:977 — number (ctor :103)
 *   - departmentTeamUser (read)              overseaBillingBase.js:978 — userId[]|null (ctor :79)
 *   - isSaveBillRate     (read)              overseaBillingBase.js:1029 — 0|1 (loadConfigValuesHandler :269)
 *   - job                (read, via getter)  overseaBillingBase.js:1028,1074,1186 — getter = this.view.job
 *
 * this.view.<prop> (VIEW surface — tabCharges MUST provide):
 *   - view.$t            (read)              overseaBillingBase.js:819,850,855,861,889,894,915-919,933,972-993 etc.
 *       i18n fn.                                                tabCharges? HAS
 *   - view.$message      (read)              overseaBillingBase.js:861,889,894,933 — HAS (Vue prototype)
 *   - view.$store.getters.currentOfficeId (read) overseaBillingBase.js:865,1040,1053,1187,1797 etc.
 *       vuex.                                                   tabCharges? HAS (this.$store)
 *   - view.settlementSelectDialog (write .accounts/.settlements/.title/.visible)
 *                                                   overseaBillingBase.js:848-851
 *       object { visible, title, accounts, settlements }.     tabCharges? MUST ADD (data)
 *   - view.overseaBillingDialog (write .createBillWay/.currentBillingBranchId/.departmentTeamUser/.isAddBillTaxAmount/.debitChargetaxRate/.billingChargeDetail/.billingChargeAccounts/.propBilling/.propBilling.charge/.job/.instructionBtns/.visible, read .createBillWay, read/write .propBilling.currency)
 *                                                   overseaBillingBase.js:974-998
 *       object.                                                 tabCharges? MUST ADD (data) — see shape below
 *   - view.newOverseaBillingDialog (write .customerDetail/.visible, read/write .propBilling.currency via overseaBillingDialog)
 *                                                   overseaBillingBase.js:995-996
 *       object { visible, customerDetail }.                    tabCharges? MUST ADD (data) — see shape below
 *
 *   NOTE: getBilling() (:1001-1036) is self-contained except for this.job (view.job), this.isSaveBillRate,
 *   this._billingCharges[0].currency. The overseas create path does NOT read view.isEdit / view.readonly /
 *   view.up.gridApi / view.btnEnable / view.permission — those are only on edit/audit/delete branches.
 *
 * ----------------------------------------------------------------------------
 * Dialog data shapes to reproduce in tabCharges (VERBATIM from source data()):
 *
 *   billDialog   (internalBillInfo.vue:601-609):
 *     billDialog: {
 *       visible: false,
 *       billingUuid: '',
 *       isView: false,
 *       createBillWay: 0,
 *       accounts: [],
 *       slaveCharges: [],
 *       instructionBtns: []
 *     }
 *     // base class ALSO writes at runtime (not declared): newBilling, newCharges,
 *     // currentBillingBranchId, departmentTeamUser. Add them to the data object for clarity:
 *     //   newBilling: {}, newCharges: [], currentBillingBranchId: 0, departmentTeamUser: null
 *
 *   newBillDialog   (internalBillInfo.vue:610-615):
 *     newBillDialog: {
 *       visible: false,
 *       customerDetail: {},
 *       billingUuid: '',
 *       isView: false
 *     }
 *
 *   settlementSelectDialog   (internalBillInfo.vue:567-572 AND overseaBilling.vue:595-600 — identical):
 *     settlementSelectDialog: {
 *       visible: false,
 *       title: '',
 *       accounts: [],
 *       settlements: []
 *     }
 *
 *   overseaBillingDialog   (overseaBilling.vue:649-656):
 *     overseaBillingDialog: {
 *       visible: false,
 *       currentBillingBranchId: 0,
 *       billingChargeDetail: [],
 *       isAddOrEdit: false,
 *       createBillWay: 0,
 *       instructionBtns: []
 *     }
 *     // base class ALSO writes at runtime (not declared): departmentTeamUser, isAddBillTaxAmount,
 *     // debitChargetaxRate, billingChargeAccounts, propBilling, job, propBilling.charge, propBilling.currency.
 *     // Add for clarity:
 *     //   departmentTeamUser: null, isAddBillTaxAmount: false, debitChargetaxRate: 0,
 *     //   billingChargeAccounts: [], propBilling: {}, job: null
 *
 *   newOverseaBillingDialog   (overseaBilling.vue:657-663):
 *     newOverseaBillingDialog: {
 *       visible: false,
 *       currentBillingBranchId: 0,
 *       billingChargeDetail: [],
 *       customerDetail: {},
 *       isAddOrEdit: false
 *     }
 *
 * ----------------------------------------------------------------------------
 * Dialog component imports (verified to resolve; webpack maps bare name -> .vue):
 *   billDialog (domestic bill)        -> @/components/Cargo/Act/billDialog.vue        (internalBillInfo.vue:288 `import BillDialog from '@/components/Cargo/Act/billDialog'`)
 *   overseaBillingDialog (overseas)   -> @/components/Cargo/Act/overseaBillingDialog.vue (overseaBilling.vue:289 `import OverseaBillingDialog from '@/components/Cargo/Act/overseaBillingDialog'`)
 *   settlementSelectDialog (both)     -> @/components/Cargo/Job/settlementSelectDialog.vue (internalBillInfo.vue:283 / overseaBilling.vue:309 `import ... from '@/components/Cargo/Job/settlementSelectDialog'`)
 *
 *   split variants (splitBillDialog / splitOverseaBillingDialog) are used by the *edit/view* branches
 *   (bill.splitBillNo) and the newBillDialog/newOverseaBillingDialog `customerDetail` path — NOT needed
 *   for the create-from-charges flow unless customerDetail.currencyRequirements > 1. Keep optional.
 *
 * ----------------------------------------------------------------------------
 * selectItemWinHandler bodies (VERBATIM — the settlement-select-dialog @selectItemWinHandler callback):
 *
 *   domestic  (internalBillInfo.vue:1160-1161):
 *     selectItemWinHandler(accountId, createBillWay) {
 *       this.pm.loadAccountCompany(accountId, createBillWay)
 *     }
 *
 *   overseas  (overseaBilling.vue:1543-1544):
 *     selectItemWinHandler(accountId, createBillWay) {
 *       this.pm.openBillingWindow(accountId, createBillWay)
 *     }
 *
 *   => tabCharges must wire its own <settlement-select-dialog @selectItemWinHandler="..."> to call
 *      pm.loadAccountCompany (domestic) / pm.openBillingWindow (overseas) accordingly.
 *
 * ----------------------------------------------------------------------------
 * Template usage contracts (props the dialogs read — so the bound data must exist):
 *
 *   <bill-dialog> (internalBillInfo.vue:117-136) binds:
 *     :visible.sync, :billing-uuid, :is-view, :new-billing, :new-charges, :department-team-user,
 *     :current-billing-branch-id, :create-bill-way, :accounts, :slave-charges, :instruction-btns
 *     + events @applyInvoice / @viewInvoice (handled by the host component)
 *
 *   <settlement-select-dialog> (internalBillInfo.vue:115 / overseaBilling.vue:263) binds:
 *     :visible.sync, :title, :accounts, :settlements, :source ('internalBillInfo' | 'overseaBilling'),
 *     @selectItemWinHandler
 *
 *   <oversea-billing-dialog> (overseaBilling.vue:126-144) binds:
 *     :visible.sync, :job, :prop-billing, :is-add-or-edit, :debit-chargetax-rate,
 *     :prop-billing-charge-accounts, :billing-charge-detail, :current-billing-branch-id,
 *     :department-team-user, :create-bill-way, :instruction-btns
 *     + events @applyInvoice / @viewInvoice
 *
 * ----------------------------------------------------------------------------
 * PROPERTIES tabCharges MUST ADD (it does NOT have these today):
 *   data() fields:
 *     - settlementSelectDialog   { visible, title, accounts, settlements }      (both paths)
 *     - billDialog               { ...see shape, incl. runtime-written fields } (domestic)
 *     - newBillDialog            { visible, customerDetail, billingUuid, isView }(domestic)
 *     - overseaBillingDialog     { ...see shape, incl. runtime-written fields } (overseas)
 *     - newOverseaBillingDialog  { ...see shape }                               (overseas)
 *   pm instances (created()/data):
 *     - pm          = new InternalBillInfoBase(this)     (domestic)
 *     - pmOversea   = new OverseaBillingBase(this)        (overseas)
 *   method:
 *     - selectItemWinHandler(accountId, createBillWay)   (routes to pm.loadAccountCompany / pm.openBillingWindow)
 *   template:
 *     - mount <bill-dialog>, <settlement-select-dialog>, <oversea-billing-dialog> (and optionally the
 *       split/new-bill dialogs) with the prop bindings above.
 *
 * PROPERTIES tabCharges ALREADY HAS (no change needed):
 *   - $t, $message, $confirm, $store (incl. $store.getters.currentOfficeId / userId / isAdmin),
 *     $checkPermission (via global mixin / Vue.prototype — both base ctors bind it from view),
 *     $refs, $emit, $i18n, $nextTick.
 *   - job (read by pm.job getter = view.job). tabCharges exposes `this.job` via tabChargesHP/context.
 *     VERIFY the exact accessor the next task uses (see UNCLEAR #1).
 *
 * ----------------------------------------------------------------------------
 * UNCLEAR / may need decoupling (FLAG for next task):
 *
 *   #1 — this.job getter path. Both base classes define `get job() { return this.view.job }`.
 *        In internalBillInfo.vue / overseaBilling.vue `job` is a top-level data() field
 *        (data.job = new Job().fillDefaultValue().getData()). In tabCharges.vue the job is NOT a
 *        direct data field — it is reached through the context/tabChargesHP. The next task must
 *        expose `job` on the tabCharges instance (a computed returning the active job, or a data
 *        alias) so that pm.job resolves. If tabCharges already has a `job` computed/data, reuse it;
 *        otherwise ADD a computed `job`. (MUST ADD (computed) pending confirmation.)
 *
 *   #2 — getSlaveInternalCharges() (domestic, internalBillInfoBase.js:648-695) calls
 *        BillingUtils.getBillTabCharges(this.view, this.job, true, false, otherParams, filterFunc).
 *        getBillTabCharges walks `this.view` expecting bill-tab charge-grid structures
 *        (e.g. this.view.tabCost / this.view.up / modifiedData). This is the RISKIEST coupling:
 *        it assumes the host has the charges-tab grid machinery that internalBillInfo.vue has.
 *        tabCharges uses a DIFFERENT charge grid (tabCost/tabSelling via tabChargesHP), NOT the
 *        bill tab's grid. => openBillingWindow's slaveCharges fetch may break or return [].
 *        Mitigation options for the next task: (a) feed [] / a precomputed slave charge list,
 *        (b) wrap getSlaveInternalCharges so it reads tabCharges' own grids, or
 *        (c) accept slaveCharges=[] for the create-from-charges flow (slave charges only matter for
 *        small-ticket grouping). Recommend (c) unless commission/group billing is required.
 *
 *   #3 — ReconciliationUtils.checkChargesIsSameInvoiceTitleAndSettlementAgent(item, buildCharges, this, true)
 *        (domestic, internalBillInfoBase.js:909) passes the base instance as the 3rd arg; it may call
 *        back into base/view APIs not enumerated here. Next task should grep that util for any
 *        this.view.* / arg.* reads before relying on it. Low risk (it is a pure validation util in
 *        practice) but UNVERIFIED in this audit.
 *
 *   #4 — view.$alert (overseas). OverseaBillingBase ctor (overseaBillingBase.js:64) binds
 *        this.$alert = this.view.$alert. The create path (checkRoleBillingCharge/getBilling/
 *        openBillingWindow) does NOT appear to call $alert, but it is bound at construction.
 *        $alert is an Element/Vue prototype method, so tabCharges HAS it. No action; noted for
 *        completeness.
 *
 *   #5 — view.isEdit / view.readonly / view.up.gridApi / view.btnEnable / view.permission /
 *        view.controllerVisible / view.selectedDataList / view.chargeList / view.selectedBilling /
 *        view.billingList / view.location / view.searchItemModule / view.createOverseaCharge /
 *        view.viewSource.
 *        NONE of these are read by the create-bill paths audited here (addRoleCharge→openBillingWindow
 *        domestic; checkRoleBillingCharge→getBilling→openBillingWindow overseas). They are only on
 *        the load/edit/delete/button-control branches. So tabCharges does NOT need to add them for
 *        create-from-charges. (Exception: view.viewSource is read by onAddClickEvent, but Option A
 *        bypasses onAddClickEvent and sets pm._billingCharges directly, so not required.)
 *        => CONFIRMED not required for the create flow. Listed here so the next task does not
 *        over-build.
 *
 *   #6 — Domestic entry seeding: the base ctor initializes _isAr=false. The create flow requires the
 *        caller to set pm._isAr (true for AR / 应收, false for AP / 应付) BEFORE addRoleCharge(charges).
 *        addBillingHandler does this at :628; Option A must replicate: `pm._isAr = isAr`.
 *
 *   #7 — cache/permission flags (enableCommission, isAddBillTaxAmount, debitChargetaxRate,
 *        isSaveBillRate, isControlled, isCostControlled, *ChargeRole, editByAll/Dep, etc.) are set in
 *        initializeHandler()/loadConfigValuesHandler(), which the bill tabs call on mount. Option A
 *        does NOT need the full initializeHandler (it touches view.cbShow*, loadBilling, grid, etc.),
 *        BUT the create path reads enableCommission / isAddBillTaxAmount / debitChargetaxRate /
 *        isSaveBillRate. The next task should either (a) call initializeHandler()-equivalent minimal
 *        setup for those flags, or (b) accept their ctor defaults (enableCommission=false,
 *        isAddBillTaxAmount=false, debitChargetaxRate=0, isSaveBillRate=0) which yields the
 *        non-commission, no-tax-amount path. Confirm the deployment's expectations.
 */
// This file is intentionally comment-only (P2-T1 audit). The next task (P2-T3/T4) adds the
// createBillingFromCharges helper code below this block.

// Validity gate mirroring internalBillInfoBase.checkBillCharge (common conditions).
export function isChargeBillable(charge, isAr) {
  if (!charge) return false
  return !charge.billingUuid &&
    charge.receivable === isAr &&
    charge.accountId > 0 &&
    !charge.commission &&
    !charge.advance &&
    !charge.shared &&
    (charge.offsetState == null || charge.offsetState <= 0)
}

// Split a selection into domestic / oversea billable groups; count the rest.
export function groupChargesForBilling(charges, isAr) {
  const domestic = []
  const oversea = []
  for (const c of charges) {
    if (!isChargeBillable(c, isAr)) continue
    ;(c.oversea ? oversea : domestic).push(c)
  }
  return { domestic, oversea, skipped: charges.length - domestic.length - oversea.length }
}

function splitRawCharges(charges) {
  const newCharges = []
  const editCharges = []
  for (const charge of charges || []) {
    if (!charge || !charge._charge) continue
    if (charge._charge.uuid) {
      editCharges.push(charge._charge)
    } else {
      newCharges.push(charge._charge)
    }
  }
  return { newCharges, editCharges }
}

export function buildDirectInternalBillingPayload(billing, charges, options = {}) {
  const { newCharges, editCharges } = splitRawCharges(charges)
  return Object.assign({}, billing, {
    createCharges: charges || [],
    newCharges,
    editCharges,
    currentOfficeId: options.currentOfficeId,
    location: options.location,
    affirm: options.affirm === true
  })
}

export function buildDirectOverseaBillingPayload(billing, charges, options = {}) {
  const { newCharges, editCharges } = splitRawCharges(charges)
  return Object.assign({}, billing, {
    charge: charges || [],
    updateCharges: charges || [],
    newCharges,
    editCharges,
    location: options.location,
    branchId: options.branchId,
    inputBy: billing && billing.inputBy ? billing.inputBy : options.inputBy,
    affirm: options.affirm === true
  })
}

export function resolveOverseaSettlementAgentId(billing, charges, settlementAgents) {
  const accountId = Number(billing && billing.accountId)
  const legalAgents = settlementAgents && settlementAgents.length ? settlementAgents : []
  const legalIds = legalAgents.map(agent => Number(agent.cmpIdx))
  const currentId = Number(billing && billing.settlementAgentId)
  if (currentId && legalIds.includes(currentId)) {
    return currentId
  }

  const charge = (charges || []).find(item => Number(item.accountId) === accountId && item.settlementAgentId)
  const chargeSettlementAgentId = Number(charge && charge.settlementAgentId)
  if (chargeSettlementAgentId && legalIds.includes(chargeSettlementAgentId)) {
    return chargeSettlementAgentId
  }

  if (legalIds.length > 0) {
    return legalIds[0]
  }
  return accountId
}
